/**
 * useOnboarding Hook
 * Manages onboarding state, navigation, and localStorage persistence
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { UseOnboardingReturn, SwipeHandlers } from './types';
import { ONBOARDING_PAGES, LOCAL_STORAGE_KEY } from './onboardingData';
import { preloadCriticalPages, preloadNextPage } from './imagePreloader';

const SWIPE_THRESHOLD = 50; // Minimum swipe distance in pixels
const TRANSITION_DURATION = 300; // Animation duration in ms

export const useOnboarding = (): UseOnboardingReturn => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
  });

  // Touch/Swipe handling
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const totalPages = ONBOARDING_PAGES.length;

  /**
   * Preload critical pages on mount
   */
  useEffect(() => {
    preloadCriticalPages();
  }, []);

  /**
   * Preload next page when current page becomes active (~100ms delay)
   */
  useEffect(() => {
    if (!hasCompletedOnboarding && currentPage < totalPages - 1) {
      preloadNextPage(currentPage);
    }
  }, [currentPage, totalPages, hasCompletedOnboarding]);

  /**
   * Navigate to specific page with transition animation
   */
  const goToPage = useCallback((page: number) => {
    if (page < 0 || page >= totalPages || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentPage(page);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, TRANSITION_DURATION);
  }, [totalPages, isTransitioning]);

  /**
   * Complete onboarding - just mark as seen, stay on page
   * The GetStarted component will handle showing Page 6 content
   */
  const completeOnboarding = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    setHasCompletedOnboarding(true);
    // Don't navigate - let the GetStarted page show Page 6 design with Get Started/Log In buttons
  }, []);

  /**
   * Navigate to next page (or complete if on last page)
   */
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1);
    } else {
      // On last page, clicking "Get Started" should complete onboarding
      completeOnboarding();
    }
  }, [currentPage, totalPages, goToPage, completeOnboarding]);

  /**
   * Navigate to previous page
   */
  const goToPrevPage = useCallback(() => {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  /**
   * Skip onboarding and mark as completed
   */
  const skipOnboarding = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    setHasCompletedOnboarding(true);
    // Stay on GetStarted page but hide carousel
  }, []);

  /**
   * Handle touch start
   */
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  /**
   * Handle touch move
   */
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  /**
   * Handle touch end - detect swipe direction
   */
  const handleTouchEnd = useCallback(() => {
    const swipeDistance = touchStartX.current - touchEndX.current;
    
    if (Math.abs(swipeDistance) > SWIPE_THRESHOLD) {
      if (swipeDistance > 0) {
        // Swiped left - go to next page
        goToNextPage();
      } else {
        // Swiped right - go to previous page
        goToPrevPage();
      }
    }
    
    // Reset
    touchStartX.current = 0;
    touchEndX.current = 0;
  }, [goToNextPage, goToPrevPage]);

  /**
   * Keyboard navigation
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (hasCompletedOnboarding) return;
      
      // Disable keyboard navigation on last page to prevent accidental triggers
      const isLastPage = currentPage === totalPages - 1;
      
      switch (e.key) {
        case 'ArrowRight':
          if (!isLastPage) {
            goToNextPage();
          }
          break;
        case 'ArrowLeft':
          goToPrevPage();
          break;
        case 'Escape':
          skipOnboarding();
          break;
        case 'Enter':
          // Prevent Enter from triggering buttons on page transitions
          if (isLastPage) {
            e.preventDefault();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasCompletedOnboarding, currentPage, totalPages, goToNextPage, goToPrevPage, skipOnboarding]);

  const swipeHandlers: SwipeHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd
  };

  return {
    currentPage,
    totalPages,
    goToNextPage,
    goToPrevPage,
    goToPage,
    skipOnboarding,
    completeOnboarding,
    hasCompletedOnboarding,
    isTransitioning,
    swipeHandlers
  };
};

